// This is the javascript entrypoint for the header section.
// This file and all its inclusions will be processed through esbuild

import '@archetype-themes/scripts/config';
import '@archetype-themes/scripts/modules/disclosure';
import '@archetype-themes/scripts/modules/mobile-nav';
import '@archetype-themes/scripts/modules/header-search';
import '@archetype-themes/scripts/modules/header-nav';
import HeaderCart from '@archetype-themes/scripts/modules/cart-drawer';
import '@archetype-themes/scripts/helpers/utils';
import '@archetype-themes/scripts/helpers/size-drawer';

class HeaderSection extends HTMLElement {
  constructor() {
    super();

    this.namespace = '.header';
    this.headerFooter = this.querySelector('#MobileNavFooter');
    this.footerMenus = document.querySelector('#FooterMenus');
    this.container = this;
    this.sectionID = this.container.getAttribute('data-section-id');
    this.detailsEl = this.container.querySelectorAll('[data-section-type="header"] details[data-hover="true"]');

    this.init();

    document.dispatchEvent(new CustomEvent('header-section:loaded', {
      detail: {
        sectionID: this.sectionID
      }, bubbles: true
    }));
  }

  init() {
    if (Shopify && Shopify.designMode) {
      // Set a timer to resize the header in case the logo changes size
      setTimeout(function() {
        window.dispatchEvent(new Event('resize'));
      }, 500);
    }

    theme.headerNav.init();
    theme.headerSearch.init();

    this.hoverMenu();

    // Enable header cart drawer when not on cart page
    if (!document.body.classList.contains('template-cart')) {
      new HeaderCart();
    }
    new theme.MobileNav({
      id: 'MobileNav',
      inHeader: true
    });

    if (theme.config.bpSmall) {
      this.cloneFooter();
    }

    window.on('resize' + this.namespace, theme.utils.debounce(300, theme.sizeDrawer));
  }

  /*
    CT: hover voor het megamenu (6-10: sneller, David).

    Openen (nog niets open)
      Na een vaste OPEN_DELAY ms op het item. Kort genoeg om direct te
      voelen, lang genoeg dat een snelle beweging over de balk (naar het
      winkelmandje) niets opent. Verlaat de muis het item eerder, dan
      wordt het openen geannuleerd. (Eerdere versie wachtte tot de muis
      stilstond; dat voelde traag.)

    Wisselen (er staat al een paneel open)
      Direct, en zonder animatie (data-ct-instant, zie ct-nav.css §1),
      zodat overschakelen tussen panelen niet flikkert.

    Sluiten
      CLOSE_GRACE ms speling na mouseleave: een schuine beweging die even
      buiten het item komt klapt het paneel niet dicht, en tijdens die
      speling geldt het menu nog als open voor direct wisselen.

    Scrollen
      Geen hover-open tijdens of vlak na scrollen (SCROLL_QUIET ms):
      browsers vuren muis-events als de pagina onder een stilstaande cursor
      schuift.

    Klikken
      Klik op een dicht item opent het; klik op een open item navigeert
      naar de link van het item.

    aria-expanded zet header-nav.js op de summary via het toggle-event.
  */
  hoverMenu() {
    const OPEN_DELAY = 80;
    const CLOSE_GRACE = 150;
    const SCROLL_QUIET = 250;

    const details = Array.from(this.detailsEl);
    if (!details.length) return;

    let lastScroll = 0;
    let openTimer = null;
    let closeTimer = null;

    window.addEventListener('scroll', () => {
      lastScroll = Date.now();
    }, { passive: true });

    const anyOpen = () => details.some((d) => d.hasAttribute('open'));

    const clearOpen = () => {
      if (openTimer) {
        clearTimeout(openTimer);
        openTimer = null;
      }
    };

    const clearClose = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    };

    const close = (detail) => {
      detail.removeAttribute('open');
      detail.removeAttribute('data-ct-instant');
    };

    // instant: zonder animatie openen (bij wisselen tussen panelen)
    const openOnly = (detail, instant) => {
      clearOpen();
      clearClose();
      details.forEach((other) => {
        if (other !== detail && other.hasAttribute('open')) close(other);
      });
      if (instant) {
        detail.setAttribute('data-ct-instant', '');
      } else {
        detail.removeAttribute('data-ct-instant');
      }
      if (!detail.hasAttribute('open')) {
        detail.setAttribute('open', '');
      }
    };

    const closeAll = () => {
      clearOpen();
      clearClose();
      details.forEach((d) => {
        if (d.hasAttribute('open')) close(d);
      });
    };

    details.forEach((detail) => {
      const summary = detail.querySelector('summary');
      const summaryLink = summary.dataset.link;

      summary.addEventListener('click', (e) => {
        e.preventDefault();

        if (!detail.hasAttribute('open')) {
          openOnly(detail, anyOpen());
        } else {
          window.location.href = summaryLink;
        }
      });

      detail.addEventListener('mouseenter', () => {
        // Terug op hetzelfde paneel binnen de speling: open laten.
        if (detail.hasAttribute('open')) {
          clearClose();
          return;
        }

        if (Date.now() - lastScroll < SCROLL_QUIET) return;

        // Menu is al actief: direct wisselen, zonder animatie.
        if (anyOpen()) {
          openOnly(detail, true);
          return;
        }

        clearOpen();
        openTimer = setTimeout(() => {
          openTimer = null;
          if (Date.now() - lastScroll < SCROLL_QUIET) return;
          openOnly(detail, false);
        }, OPEN_DELAY);
      });

      detail.addEventListener('mouseleave', () => {
        clearOpen();
        if (!detail.hasAttribute('open')) return;

        clearClose();
        closeTimer = setTimeout(() => {
          closeTimer = null;
          close(detail);
        }, CLOSE_GRACE);
      });

      detail.addEventListener('focusout', (e) => {
        if (!detail.contains(e.relatedTarget)) {
          close(detail);
        }
      });

      detail.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && detail.hasAttribute('open')) {
          closeAll();
          summary.focus();
        }
      });
    });
  }

  cloneFooter() {
    if (!this.headerFooter) {
      return;
    }

    const clone = this.footerMenus.cloneNode(true);
    clone.id = '';

    // Append cloned footer menus to mobile nav
    this.headerFooter.appendChild(clone);

    // If localization form, update IDs so they don't match footer
    const localizationForm = this.headerFooter.querySelector('.multi-selectors');
    if (localizationForm) {
      // Loop disclosure buttons and update ids and aria attributes
      localizationForm.querySelectorAll('[data-disclosure-toggle]').forEach(el => {
        const controls = el.getAttribute('aria-controls');
        const describedby = el.getAttribute('aria-describedby')

        el.setAttribute('aria-controls', controls + '-header');
        el.setAttribute('aria-describedby', describedby + '-header');

        const list = document.getElementById(controls);
        if (list) {
          list.id = controls + '-header';
        }

        const label = document.getElementById(describedby);
        if (label) {
          label.id = describedby + '-header';
        }

        // Initialize language/currency selectors
        const parent = el.parentNode;
        if (parent) {
          new theme.Disclosure(parent);
        }
      });
    }
  }
}

customElements.define('header-section', HeaderSection);
